class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    const mode = this.dataset.mode || 'slider';

    if (mode === 'scroll') {
      this.initScroll();
    } else {
      this.initSlider();
    }

    this.setAttribute('loaded', '');
  }

  initSlider() {
    this.beforeEl = this.querySelector('.before-after-slider__before');
    this.handle = this.querySelector('.before-after-slider__handle');
    if (!this.beforeEl || !this.handle) return;

    this.isDragging = false;
    this.position = parseFloat(getComputedStyle(this).getPropertyValue('--initial-position')) || 50;

    this.updateSliderWidth();
    this.updatePosition(this.position);

    // Mouse events
    this.handle.addEventListener('mousedown', this.onDragStart.bind(this));
    this.addEventListener('mousemove', this.onDragMove.bind(this));
    document.addEventListener('mouseup', this.onDragEnd.bind(this));

    // Touch events
    this.handle.addEventListener('touchstart', this.onDragStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onDragEnd.bind(this));

    // Keyboard events
    this.handle.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  initScroll() {
    this.tracks = this.querySelectorAll('.before-after-slider__scroll-track');
    if (!this.tracks.length) return;

    this.paused = false;
    this.pauseBtn = this.querySelector('.before-after-slider__pause-btn');

    const setPaused = (paused) => {
      this.paused = paused;
      this.tracks.forEach(t => t.style.animationPlayState = paused ? 'paused' : 'running');
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-label', paused ? 'Play scrolling animation' : 'Pause scrolling animation');
        this.pauseBtn.innerHTML = paused
          ? '<span aria-hidden="true">&#9654;</span>'
          : '<span aria-hidden="true">&#10074;&#10074;</span>';
      }
    };

    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => setPaused(!this.paused));
    }

    // Hover pause (mouse only, don't override manual pause)
    this._hoverPaused = false;
    this.addEventListener('mouseenter', () => {
      if (!this.paused) { setPaused(true); this._hoverPaused = true; }
    });
    this.addEventListener('mouseleave', () => {
      if (this._hoverPaused) { setPaused(false); this._hoverPaused = false; }
    });
  }

  onDragStart(e) {
    this.isDragging = true;
    this.classList.add('is-dragging');
    if (e.type === 'mousedown') e.preventDefault();
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    const rect = this.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    this.updatePosition(percent);
  }

  onTouchMove(e) {
    if (!this.isDragging) return;
    const touch = e.touches[0];
    const rect = this.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

    // Only prevent scroll if dragging predominantly horizontal
    if (this.lastTouchX !== undefined) {
      const dx = Math.abs(touch.clientX - this.lastTouchX);
      const dy = Math.abs(touch.clientY - this.lastTouchY);
      if (dx > dy) e.preventDefault();
    }
    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;

    this.updatePosition(percent);
  }

  onDragEnd() {
    this.isDragging = false;
    this.classList.remove('is-dragging');
    this.lastTouchX = undefined;
    this.lastTouchY = undefined;
  }

  onKeyDown(e) {
    const step = 5;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      this.updatePosition(Math.max(0, this.position - step));
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.updatePosition(Math.min(100, this.position + step));
    }
  }

  updateSliderWidth() {
    this.style.setProperty('--slider-width', `${this.offsetWidth}px`);
  }

  updatePosition(percent) {
    this.position = percent;
    this.style.setProperty('--position', `${percent}%`);
    this.handle.setAttribute('aria-valuenow', Math.round(percent));
  }
}

customElements.define('before-after-slider', BeforeAfterSlider);
